import { useCallback, useEffect, useRef, useState } from "react";
import { analyzeAudio, type PeakSet } from "../lib/audioMeta";
import { formatTime } from "../lib/media";

interface CommentMarker {
  id: string;
  timestampSec: number;
  status: string;
  authorRole?: string;
  text?: string;
}

interface Props {
  audioUrl?: string;
  currentTime: number;
  duration: number;
  isPlaying?: boolean;
  comments?: CommentMarker[];
  onSeek: (time: number) => void;
  onAddComment?: (timestampSec: number) => void;
  onMeta?: (meta: {
    durationSec?: number;
    sampleRate?: number;
    channels?: number;
    sizeBytes?: number;
    bitrateKbps?: number;
    mimeType?: string;
  }) => void;
  height?: number;
  bars?: number;
}

function emptyPeaks(barCount: number): PeakSet {
  return {
    peaks: new Float32Array(barCount),
    rms: new Float32Array(barCount),
  };
}

export default function Waveform({
  audioUrl,
  currentTime,
  duration,
  isPlaying = false,
  comments = [],
  onSeek,
  onAddComment,
  onMeta,
  height = 112,
  bars,
}: Props) {
  const shellRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef(false);

  const [width, setWidth] = useState(640);
  const [data, setData] = useState<PeakSet>(() => emptyPeaks(160));
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const barCount = bars ?? Math.max(96, Math.min(280, Math.round(width / 3.2)));

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 640;
      setWidth(Math.max(240, Math.floor(w)));
    });
    ro.observe(el);
    setWidth(Math.max(240, Math.floor(el.clientWidth || 640)));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!audioUrl) {
      setData(emptyPeaks(barCount));
      setReady(false);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setReady(false);
    setError(null);
    setData(emptyPeaks(barCount));

    analyzeAudio(audioUrl, barCount)
      .then((analysis) => {
        if (cancelled) return;
        setData(analysis.peaks);
        setReady(true);
        onMeta?.(analysis.meta);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError("Waveform unavailable");
        setData(emptyPeaks(barCount));
        setReady(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [audioUrl, barCount, onMeta]);

  const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, width);
    const h = Math.max(48, height);
    if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, "#0a0f18");
    bg.addColorStop(0.5, "#070b12");
    bg.addColorStop(1, "#05070d");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.045)";
    ctx.lineWidth = 1;
    const grid = 8;
    for (let i = 1; i < grid; i++) {
      const x = (w / grid) * i;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();
    ctx.restore();

    const count = data.peaks.length || barCount;
    const gap = count > 180 ? 0.6 : 1;
    const slot = w / count;
    const barW = Math.max(1.2, slot - gap);
    const mid = h / 2;
    const maxHalf = h * 0.42;
    const playedX = progress * w;

    for (let i = 0; i < count; i++) {
      const peak = data.peaks[i] || 0;
      const body = data.rms[i] || peak * 0.7;
      const x = i * slot + (slot - barW) / 2;
      const peakH = Math.max(1.5, peak * maxHalf);
      const bodyH = Math.max(1, body * maxHalf * 0.92);
      const played = x + barW / 2 <= playedX;

      ctx.fillStyle = played ? "rgba(120, 255, 132, 0.95)" : "rgba(22, 187, 27, 0.38)";
      roundRect(ctx, x, mid - peakH, barW, peakH * 2, Math.min(2, barW / 2));
      ctx.fill();

      ctx.fillStyle = played ? "rgba(22, 187, 27, 0.88)" : "rgba(22, 187, 27, 0.22)";
      roundRect(ctx, x, mid - bodyH, barW, bodyH * 2, Math.min(2, barW / 2));
      ctx.fill();
    }

    if (playedX > 0) {
      const glow = ctx.createLinearGradient(0, 0, playedX, 0);
      glow.addColorStop(0, "rgba(22,187,27,0.00)");
      glow.addColorStop(1, "rgba(22,187,27,0.08)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, playedX, h);
    }

    if (duration > 0) {
      const px = playedX;
      ctx.save();
      ctx.shadowColor = "rgba(255,255,255,0.65)";
      ctx.shadowBlur = 8;
      ctx.strokeStyle = isPlaying ? "#ffffff" : "rgba(255,255,255,0.9)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, 4);
      ctx.lineTo(px, h - 4);
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(px, 8, 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    if (loading && !ready) {
      const t = (performance.now() / 900) % 1;
      const shimmer = ctx.createLinearGradient(0, 0, w, 0);
      const a = Math.max(0, t - 0.25);
      const b = t;
      const c = Math.min(1, t + 0.25);
      shimmer.addColorStop(0, "rgba(255,255,255,0)");
      shimmer.addColorStop(a, "rgba(255,255,255,0)");
      shimmer.addColorStop(b, "rgba(255,255,255,0.08)");
      shimmer.addColorStop(c, "rgba(255,255,255,0)");
      shimmer.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = shimmer;
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "rgba(22,187,27,0.12)";
      ctx.fillRect(0, mid - 2, w, 4);
    }
  }, [width, height, data, progress, duration, isPlaying, loading, ready, barCount]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    if (!loading || ready) return;
    let raf = 0;
    const tick = () => {
      draw();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loading, ready, draw]);

  function timeFromClientX(clientX: number): number {
    if (!trackRef.current || duration <= 0) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return pct * duration;
  }

  function seekFromEvent(clientX: number) {
    if (duration <= 0) return;
    onSeek(timeFromClientX(clientX));
  }

  return (
    <div className="waveform-shell" ref={shellRef}>
      <div className="timeline-time-labels">
        <span>{formatTime(currentTime)}</span>
        <span className="waveform-status">
          {loading && !ready
            ? "Building real waveform…"
            : error
              ? error
              : duration > 0
                ? formatTime(duration)
                : "—"}
        </span>
      </div>

      <div
        ref={trackRef}
        className={`waveform-track canvas-waveform ${ready ? "ready" : "loading"} ${isPlaying ? "playing" : ""}`}
        style={{ height }}
        onPointerDown={(e) => {
          dragRef.current = true;
          (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
          seekFromEvent(e.clientX);
        }}
        onPointerMove={(e) => {
          if (duration > 0) setHoverTime(timeFromClientX(e.clientX));
          if (dragRef.current) seekFromEvent(e.clientX);
        }}
        onPointerUp={(e) => {
          dragRef.current = false;
          try {
            (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
          } catch {
            // ignore
          }
        }}
        onPointerCancel={() => {
          dragRef.current = false;
        }}
        onPointerLeave={() => {
          setHoverTime(null);
          dragRef.current = false;
        }}
        role="slider"
        aria-label="Audio waveform"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, Math.floor(duration))}
        aria-valuenow={Math.floor(currentTime)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") onSeek(Math.max(0, currentTime - 5));
          if (e.key === "ArrowRight") onSeek(Math.min(duration, currentTime + 5));
          if (e.key === "Home") onSeek(0);
          if (e.key === "End") onSeek(duration);
        }}
      >
        <canvas ref={canvasRef} className="waveform-canvas" />

        {hoverTime !== null && duration > 0 ? (
          <div
            className="waveform-hover"
            style={{ left: `${(hoverTime / duration) * 100}%` }}
          >
            <span>{formatTime(hoverTime)}</span>
          </div>
        ) : null}

        {comments.map((c, index) => (
          <button
            key={c.id}
            type="button"
            className={`waveform-marker ${c.status}`}
            style={{
              left: `${
                duration > 0
                  ? Math.min(100, Math.max(0, (c.timestampSec / duration) * 100))
                  : 0
              }%`,
              bottom: `${8 + (index % 2) * 28}px`,
            }}
            aria-label={`${c.authorRole === "admin" ? "Producer" : "Artist"} comment at ${formatTime(c.timestampSec)}${c.text ? `: ${c.text}` : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onSeek(c.timestampSec);
            }}
          >
            <span className="waveform-marker-avatar" aria-hidden="true">
              {c.authorRole === "admin" ? "P" : "A"}
            </span>
            <span className="waveform-marker-preview">
              <strong>{formatTime(c.timestampSec)}</strong>
              {c.text ? <span>{c.text}</span> : null}
            </span>
          </button>
        ))}
      </div>

      {onAddComment ? (
        <button
          type="button"
          className="timeline-add-comment"
          onClick={() => onAddComment(Math.round(currentTime))}
        >
          Add comment at playhead
        </button>
      ) : null}
    </div>
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
